import { useState, useRef, useEffect, useCallback } from 'react';
import { FlatList } from 'react-native';

import type { CarouselMedia } from './carouselMedia';

interface UseCarouselStateOptions {
  items: CarouselMedia[];
  initialIndex: number;
  visible: boolean;
  screenWidth: number;
}

interface UseCarouselStateReturn {
  currentIndex: number;
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
  flatListRef: React.RefObject<FlatList>;
  getItemLayout: (
    _: unknown,
    index: number,
  ) => { length: number; offset: number; index: number };
  scrollToIndex: (index: number, animated?: boolean) => void;
}

/**
 * Shared carousel state management hook.
 * Extracts common logic between native and web carousel modals.
 *
 * @param options - Configuration options
 * @returns Carousel state and utilities
 */
export const useCarouselState = ({
  items,
  initialIndex,
  visible,
  screenWidth,
}: UseCarouselStateOptions): UseCarouselStateReturn => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const flatListRef = useRef<FlatList>(null);

  // Reset scroll position when the modal opens with a new initialIndex
  useEffect(() => {
    if (visible) {
      setCurrentIndex(initialIndex);
      requestAnimationFrame(() => {
        flatListRef.current?.scrollToIndex({
          index: initialIndex,
          animated: false,
        });
      });
    }
  }, [visible, initialIndex]);

  const getItemLayout = useCallback(
    (_: unknown, index: number) => ({
      length: screenWidth,
      offset: screenWidth * index,
      index,
    }),
    [screenWidth],
  );

  const scrollToIndex = useCallback(
    (index: number, animated = true) => {
      const clamped = Math.max(0, Math.min(index, items.length - 1));
      setCurrentIndex(clamped);
      flatListRef.current?.scrollToIndex({ index: clamped, animated });
    },
    [items.length],
  );

  return {
    currentIndex,
    setCurrentIndex,
    flatListRef,
    getItemLayout,
    scrollToIndex,
  };
};
